import {XIcon} from '@primer/octicons-react'
import {Heading, IconButton} from '@primer/react'
import {observeClientInteraction} from '@github-ui/ahp-relay/relay-command-observation'
import {useEffect, useRef, useState} from 'react'

import styles from './AgentIcebreakers.module.css'
import {agentIcebreakers, getIcebreakers} from './icebreakers'
import type {IcebreakerOption} from './icebreaker'

export interface AgentIcebreakersProps {
  onSelect: (option: IcebreakerOption, event?: Event) => void
  onCommit?: (event: Event) => void
  onDismiss: () => void
  /** Retained for callers with legacy experiment payloads; all variants use the same pool. */
  variant?: string
  location?: string
  /** Override the default heading text. */
  heading?: string
  /** Override the default h3 heading level. */
  headingLevel?: 'h2' | 'h3'
  /** Override the default randomized icebreaker items. */
  items?: IcebreakerOption[]
}

export function AgentIcebreakers({
  onSelect,
  onCommit,
  onDismiss,
  heading,
  headingLevel = 'h3',
  items,
}: AgentIcebreakersProps) {
  const [selectedIcebreakers, setSelectedIcebreakers] = useState(() => agentIcebreakers.slice(0, 3))
  const hasSelectedIcebreakersRef = useRef(false)

  useEffect(() => {
    if (hasSelectedIcebreakersRef.current) return
    hasSelectedIcebreakersRef.current = true
    // Randomize after hydration so server and client markup agree.
    setSelectedIcebreakers(getIcebreakers())
  }, [])
  const icebreakers = items ?? selectedIcebreakers
  return (
    <div data-testid="agent-icebreakers">
      <div className={styles.header}>
        <Heading as={headingLevel} className={styles.headerTitle}>
          {heading ?? 'Get started with agents'}
        </Heading>
        <IconButton
          variant="invisible"
          size="small"
          aria-label="Dismiss suggestions"
          icon={XIcon}
          onClick={onDismiss}
        />
      </div>
      <div className={styles.container}>
        {icebreakers.map(icebreaker => (
          <button
            key={icebreaker.title}
            type="button"
            className={styles.icebreaker}
            onClick={event => {
              if (onCommit) {
                observeClientInteraction(() => onCommit(event.nativeEvent))
                onSelect(icebreaker, event.nativeEvent)
              } else {
                onSelect(icebreaker)
              }
            }}
            data-testid={`icebreaker-${icebreaker.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <span className={styles.iconWrapper}>{icebreaker.icon}</span>
            <div className={styles.textContent}>
              <span className={styles.title}>{icebreaker.title}</span>
              <span className={styles.subheader}>{icebreaker.subheader}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}
